import { ItemStatus } from "@focus/focusUI";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { ofType } from "redux-observable";
import { mergeMap, map, catchError, of } from "rxjs";
import { createAsyncReducers } from "./helpers";

const baseURL = "/api/v1/config/organization";
const baseSumURL = "/api/v1/sum";

export type SystemConfigRequestKey =
  | "getConfig"
  | "setConfig"
  | "getSchema"
  | "setSchema"
  | "getSumConfiguration";

type RequestState = {
  status: ItemStatus;
  error: string | null;
  fieldErrors?: Record<string, string> | null;
};

type RequestsMap = Record<SystemConfigRequestKey, RequestState>;

type SystemConfigPayload = {
  currentConfig: Record<string, unknown>;
};

type SystemConfigSchemaPayload = {
  schema: Record<string, unknown>;
};

type SumConfigPayload = {
  sumConfiguration: Record<string, unknown>;
};

type SystemConfigState = SystemConfigPayload &
  SystemConfigSchemaPayload &
  SumConfigPayload & {
    currentConfigOperatorName?: string;
    requests: RequestsMap;
  };

const initialState: SystemConfigState = {
  schema: undefined,
  currentConfig: undefined,
  sumConfiguration: undefined,
  currentConfigOperatorName: undefined,
  requests: {
    getConfig: { status: ItemStatus.READY, error: null },
    setConfig: { status: ItemStatus.READY, error: null },
    getSchema: { status: ItemStatus.READY, error: null },
    setSchema: { status: ItemStatus.READY, error: null },
    getSumConfiguration: {
      status: ItemStatus.READY,
      error: null,
    },
  },
};

const getSumConfigurationAsync = createAsyncReducers<
  SystemConfigState,
  SystemConfigRequestKey,
  Record<string, unknown>
>("getSumConfiguration", (state, action) => {
  state.sumConfiguration = action.payload;
});

const getSystemConfigurationAsync = createAsyncReducers<
  SystemConfigState,
  SystemConfigRequestKey,
  Record<string, unknown>,
  { operatorName: string }
>("getConfig", (state, action) => {
  state.currentConfig = action.payload;
  state.currentConfigOperatorName = action.meta?.operatorName;
});

const setSystemConfigurationAsync = createAsyncReducers<
  SystemConfigState,
  SystemConfigRequestKey,
  Record<string, unknown>,
  {
    operatorName: string;
    config: Record<string, unknown>;
  }
>("setConfig", (state, action) => {
  state.currentConfig = action.payload;
  state.currentConfigOperatorName = action.meta?.operatorName;
});

const getSystemConfigurationSchemaAsync = createAsyncReducers<
  SystemConfigState,
  SystemConfigRequestKey,
  Record<string, unknown>,
  { operatorName: string }
>("getSchema", (state, action) => {
  state.schema = action.payload;
});

const setSystemConfigurationSchemaAsync = createAsyncReducers<
  SystemConfigState,
  SystemConfigRequestKey,
  Record<string, unknown>,
  {
    operatorName: string;
    schema: Record<string, unknown>;
  }
>("setSchema", (state, action) => {
  state.schema = action.payload;
});

const systemConfigSlice = createSlice({
  name: "systemConfig",
  initialState,
  reducers: {
    getSumConfigurationRequest: getSumConfigurationAsync.request,
    getSumConfigurationSuccess: getSumConfigurationAsync.success,
    getSumConfigurationFailure: getSumConfigurationAsync.failure,
    getSystemConfigRequest: getSystemConfigurationAsync.request,
    getSystemConfigSuccess: getSystemConfigurationAsync.success,
    getSystemConfigFailure: getSystemConfigurationAsync.failure,
    saveSystemConfigRequest: setSystemConfigurationAsync.request,
    saveSystemConfigSuccess: setSystemConfigurationAsync.success,
    saveSystemConfigFailure: setSystemConfigurationAsync.failure,
    getSystemConfigSchemaRequest: getSystemConfigurationSchemaAsync.request,
    getSystemConfigSchemaSuccess: getSystemConfigurationSchemaAsync.success,
    getSystemConfigSchemaFailure: getSystemConfigurationSchemaAsync.failure,
    saveSystemConfigSchemaRequest: setSystemConfigurationSchemaAsync.request,
    saveSystemConfigSchemaSuccess: setSystemConfigurationSchemaAsync.success,
    saveSystemConfigSchemaFailure: setSystemConfigurationSchemaAsync.failure,
  },
});

export const {
  getSumConfigurationRequest,
  getSumConfigurationSuccess,
  getSumConfigurationFailure,
  getSystemConfigRequest,
  getSystemConfigSuccess,
  getSystemConfigFailure,
  saveSystemConfigRequest,
  saveSystemConfigSuccess,
  saveSystemConfigFailure,
  getSystemConfigSchemaRequest,
  getSystemConfigSchemaSuccess,
  getSystemConfigSchemaFailure,
  saveSystemConfigSchemaRequest,
  saveSystemConfigSchemaSuccess,
  saveSystemConfigSchemaFailure,
} = systemConfigSlice.actions;

const getSumConficEpic = (action$, _state$, { ajax }) =>
  action$.pipe(
    ofType(getSumConfigurationRequest.type),
    mergeMap(() =>
      ajax.getJSON(`${baseSumURL}/config`).pipe(
        map((data) => getSumConfigurationSuccess(data as SumConfigPayload)),
        catchError((error) =>
          of(
            getSumConfigurationFailure(
              error.message || "Error fetching sum configuration",
            ),
          ),
        ),
      ),
    ),
  );

const getSystemConfigEpic = (action$, _state$, { ajax }) =>
  action$.pipe(
    ofType(getSystemConfigRequest.type),
    mergeMap((action: PayloadAction<{ operatorName: string }>) => {
      const organizationName = action.payload.operatorName;
      const url = `${baseURL}/${organizationName}/data`;
      return ajax.getJSON(url).pipe(
        map((data) =>
          getSystemConfigSuccess(data as SystemConfigPayload, {
            operatorName: organizationName,
          }),
        ),
        catchError((error) =>
          of(
            getSystemConfigFailure(
              error.message || "Error fetching system configuration",
            ),
          ),
        ),
      );
    }),
  );

const setSystemConfigEpic = (action$, _state$, { ajax }) =>
  action$.pipe(
    ofType(saveSystemConfigRequest.type),
    mergeMap(
      (
        action: PayloadAction<{
          operatorName: string;
          config: Record<string, unknown>;
        }>,
      ) => {
        const { operatorName, config } = action.payload;
        const url = `${baseURL}/${operatorName}/data`;

        return ajax({
          url,
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: config,
          responseType: "json",
        }).pipe(
          mergeMap(() =>
            of(
              saveSystemConfigSuccess(config, {
                notificationOnSuccess: "Configuration updated successfully",
                operatorName,
              }),
            ),
          ),
          catchError((error) =>
            of(
              saveSystemConfigFailure(
                error.response || {
                  message: error.message || "Error updating configuration",
                },
                {
                  errorMessage:
                    error.response?.message ||
                    error.message ||
                    "Error updating configuration",
                },
              ),
            ),
          ),
        );
      },
    ),
  );

const getSystemConfiSchemagEpic = (action$, _state$, { ajax }) =>
  action$.pipe(
    ofType(getSystemConfigSchemaRequest.type),
    mergeMap((action: PayloadAction<{ operatorName: string }>) => {
      const organizationName = action.payload.operatorName;
      const url = `${baseURL}/${organizationName}/schema`;
      return ajax.getJSON(url).pipe(
        map((data) =>
          getSystemConfigSchemaSuccess(data as Record<string, unknown>),
        ),
        catchError((error) =>
          of(
            getSystemConfigSchemaFailure(error.response, {
              errorMessage:
                error.message || "Could not load system configuration",
            }),
          ),
        ),
      );
    }),
  );

const setSystemConfigSchemaEpic = (action$, _state$, { ajax }) =>
  action$.pipe(
    ofType(saveSystemConfigSchemaRequest.type),
    mergeMap(
      (
        action: PayloadAction<{
          operatorName: string;
          schema: Record<string, unknown>;
        }>,
      ) => {
        const { operatorName, schema } = action.payload;
        const url = `${baseURL}/${operatorName}/schema`;

        return ajax({
          url,
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: schema,
          responseType: "json",
        }).pipe(
          mergeMap(() =>
            of(
              saveSystemConfigSchemaSuccess(schema, {
                notificationOnSuccess: "Schema updated successfully",
              }),
            ),
          ),
          catchError((error) =>
            of(
              saveSystemConfigSchemaFailure(error.response, {
                errorMessage:
                  error.response?.message ||
                  error.message ||
                  "Error updating configuration",
              }),
            ),
          ),
        );
      },
    ),
  );

export default {
  ...systemConfigSlice,
  middleware: [
    getSumConficEpic,
    getSystemConfigEpic,
    setSystemConfigEpic,
    getSystemConfiSchemagEpic,
    setSystemConfigSchemaEpic,
  ],
};
